import { productPath } from '../services/productPath';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ShareCompareButtons from '../components/ShareCompareButtons';
import { Scale, Trash2, ShoppingCart, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import AccountLayout from '../components/AccountLayout';
import BrandLogo from '../components/BrandLogo';
import { compareStore, useCompareIds, COMPARE_MAX } from '../services/compareStore';
import { productsAPI, cartAPI, categoriesAPI } from '../services/api';
import { availabilityOf } from '../services/stock';
import { specTemplate, buildSpecRows, standardKey } from '../services/specs';
import { useT } from '../services/i18n';
import { prodName, useTerms } from '../services/catalogI18n';
import { usePageMeta } from '../services/seo';

const money = (v) => `${Number(v || 0).toFixed(2)} €`;
const norm = (s) => String(s ?? '').trim().toLowerCase();

// Technical rows: template order when the category defines one, else the union of product attributes
const specRows = (products, category) => {
  const template = specTemplate(category, products[0]?.subcategory);
  const labels = template
    ? template.filter(l => !['ref', 'price', 'brand'].includes(standardKey(l)))
    : [...new Set(products.flatMap(p => Object.keys(p.attributes || {})))];
  const valuesOf = template
    ? (p) => Object.fromEntries(buildSpecRows(p, template).map(([k, v]) => [k, v]))
    : (p) => p.attributes || {};
  const perProduct = products.map(valuesOf);
  return labels
    .filter(l => perProduct.some(v => v[l]))
    .map(l => ({ label: l, values: perProduct.map(v => v[l] ?? '—'), differs: new Set(perProduct.map(v => norm(v[l]))).size > 1 }));
};

// Side-by-side comparison of up to 3 products of the same subcategory
const ComparePage = () => {
  const { t, lang } = useT();
  const tt = useTerms(lang);
  usePageMeta({ title: t('cmp.pageTitle'), noindex: true });
  const [searchParams] = useSearchParams();
  const sharedIds = (searchParams.get('ids') || '').split(',').map(x => x.trim()).filter(Boolean).slice(0, 6);
  const ownIds = useCompareIds();
  const ids = sharedIds.length ? sharedIds : ownIds;
  const shared = sharedIds.length > 0;
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => { categoriesAPI.getAll().then(setCategories).catch(() => {}); }, []);
  useEffect(() => {
    if (!ids.length) { setProducts([]); return; }
    productsAPI.getByIds(ids).then(setProducts).catch(() => setProducts([]));
  }, [ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = async (p) => {
    try { await cartAPI.addItem(p.id, 1); toast.success(t('product.added')); } catch (e) { toast.error(e.response?.data?.detail || t('cmp.addFailed')); }
  };

  const category = products?.length ? categories.find(c => c.slug === products[0].category) : null;
  const techRows = products?.length ? specRows(products, category) : [];
  const diffCell = (differs) => (differs ? 'bg-yellow-50' : '');

  const rows = products?.length ? [
    [t('cmp.price'), p => <span className="text-xl font-bold text-gray-900">{money(p.price)}</span>],
    [t('cmp.oldPrice'), p => (p.original_price ? <span className="line-through text-gray-400">{money(p.original_price)}</span> : '—')],
    [t('cmp.discount'), p => (p.discount ? <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded">−{p.discount} %</span> : '—')],
    [t('cmp.rating'), p => (p.rating ? <span className="inline-flex items-center gap-1"><Star size={14} className="text-yellow-400 fill-yellow-400" /> {Number(p.rating).toFixed(1)} <span className="text-gray-400 text-xs">({p.reviews_count || p.reviews || 0})</span></span> : '—')],
    [t('cmp.availability'), p => { const a = availabilityOf(p); return <span className={a.status !== 'out' ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>{a.status !== 'out' ? a.label : t('cmp.outOfStock')}</span>; }],
    [t('cmp.ref'), p => <span className="font-mono text-xs">{p.ref}</span>],
    [t('tw.type'), p => p.type || '—'],
    [t('cmp.subcategory'), p => p.subcategory || '—'],
    [t('cmp.soldBy'), p => (p.sale_multiple > 1 ? `${p.sale_multiple} (${p.sale_multiple_label || t('cmp.pack')})` : t('common.unit'))],
    [t('cmp.packs'), p => (p.packs?.length ? p.packs.map(k => `${k.label} ${money(k.price)}`).join(' · ') : '—')],
    [t('cmp.warranty'), () => t('cmp.twoYears')],
    [t('cmp.compat'), p => (p.compatibility?.length ? [...new Set(p.compatibility.map(r => r.brand))].join(', ') : t('cmp.universal'))],
  ] : [];

  return (
    <AccountLayout title={t('cmp.title')} requireAuth={false} wide>
      <div className="bg-white rounded-xl shadow-sm p-6" data-testid="compare-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-sm text-gray-500 flex items-center gap-2" data-testid="compare-intro"><Scale size={16} className="text-orange-500" /> {shared ? t('cmp.shared', { n: ids.length, count: ids.length }) : t('cmp.intro2', { n: ids.length, max: COMPARE_MAX })}</p>
          <div className="flex flex-wrap items-center gap-3">
            {products?.length > 0 && <ShareCompareButtons products={products} />}
            {!shared && ids.length > 0 && <button type="button" onClick={() => compareStore.clear()} data-testid="compare-clear" className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 size={14} /> {t('cmp.clear')}</button>}
          </div>
        </div>
        {products === null ? <p className="text-sm text-gray-400 py-10 text-center">{t('common.loading')}</p> : products.length === 0 ? (
          <div className="text-center py-12" data-testid="compare-empty">
            <Scale size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium">{t('cmp.empty')}</p>
            <p className="text-sm text-gray-500 mt-1">{t('cmp.emptyHint2', { max: COMPARE_MAX })}</p>
            <Link to="/pieces" className="inline-block mt-4 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="compare-browse">{t('cmp.browse')}</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-0" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="text-left align-bottom p-3 w-28 sm:w-40 text-xs text-gray-500 uppercase">{t('cmp.colHeader')}</th>
                  {products.map(p => (
                    <th key={p.id} className="p-3 align-top text-left border-l border-gray-100 min-w-[160px] sm:min-w-[200px]" data-testid={`compare-col-${p.id}`}>
                      <div className="relative">
                        {!shared && <button type="button" onClick={() => compareStore.remove(p.id)} data-testid={`compare-remove-${p.id}`} className="absolute -top-1 -right-1 p-1 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600" title={t('cmp.remove')}><X size={13} /></button>}
                        <Link to={productPath(p)} className="block h-28 flex items-center justify-center mb-2"><img src={p.image} alt={prodName(p, lang)} className="max-h-full object-contain" /></Link>
                        <BrandLogo brand={p.brand} size="xs" />
                        <Link to={productPath(p)} className="block font-semibold text-gray-900 hover:text-orange-600 mt-1 leading-tight">{prodName(p, lang)}</Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, render]) => (
                  <tr key={label} className="odd:bg-gray-50/70">
                    <td className="p-3 font-medium text-gray-600 text-xs uppercase">{label}</td>
                    {products.map(p => <td key={p.id} className="p-3 border-l border-gray-100 text-gray-800">{render(p)}</td>)}
                  </tr>
                ))}
                {techRows.length > 0 && (
                  <tr data-testid="compare-specs-header">
                    <td colSpan={products.length + 1} className="p-3 pt-6">
                      <p className="text-sm font-bold text-gray-900">{t('cmp.specsTitle')}</p>
                      <p className="text-xs text-gray-500">{t('cmp.diffHint')}</p>
                    </td>
                  </tr>
                )}
                {techRows.map(r => (
                  <tr key={r.label} data-testid={`compare-spec-row-${r.label}`} className={r.differs ? 'bg-yellow-50' : 'odd:bg-gray-50/70'}>
                    <td className="p-3 font-medium text-gray-600 text-xs uppercase">{tt(r.label)}{r.differs && <span className="ml-1 text-[10px] text-yellow-700 font-bold" title={t('cmp.diffHint')}>≠</span>}</td>
                    {r.values.map((v, i) => <td key={products[i].id} className={`p-3 border-l border-gray-100 text-gray-800 ${diffCell(r.differs)}`}>{tt(v)}</td>)}
                  </tr>
                ))}
                <tr>
                  <td className="p-3" />
                  {products.map(p => (
                    <td key={p.id} className="p-3 border-l border-gray-100">
                      <button type="button" onClick={() => addToCart(p)} disabled={availabilityOf(p).status === 'out'} data-testid={`compare-add-${p.id}`} className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white text-sm font-semibold px-3 py-2 rounded-md"><ShoppingCart size={15} /> {t('common.addToCart')}</button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AccountLayout>
  );
};

export default ComparePage;
